import { Children, cloneElement, isValidElement, type ReactNode } from "react";
import { useRouterState } from "@tanstack/react-router";

type NodeProps = { children?: ReactNode; className?: string };
function paragraph(node: ReactNode) {
  return isValidElement<NodeProps>(node) && (node.type === "p" || (typeof node.type === "function" && node.type.name === "Body"));
}
function text(node: ReactNode): string {
  if (typeof node === "string" || typeof node === "number") return String(node);
  return isValidElement<NodeProps>(node) ? Children.toArray(node.props.children).map(text).join("") : "";
}
function slice(node: ReactNode, start: number, end: number): ReactNode {
  if (typeof node === "string" || typeof node === "number") return String(node).slice(start, end);
  if (!isValidElement<NodeProps>(node)) return null;
  let offset = 0;
  const children = Children.toArray(node.props.children).map(child => {
    const length = text(child).length;
    const result = end > offset && start < offset + length ? slice(child, Math.max(0, start - offset), Math.min(length, end - offset)) : null;
    offset += length;
    return result;
  });
  return cloneElement(node, { children });
}
function traverse(node: ReactNode, visit: (node: ReactNode) => ReactNode): ReactNode {
  if (paragraph(node)) return visit(node);
  if (!isValidElement<NodeProps>(node) || !node.props.children) return node;
  // Lists, cards and framed tools are not prose paragraphs.
  if (typeof node.type === "string" && !["div", "section"].includes(node.type)) return node;
  if (/grid|cards|faq-item/.test(node.props.className ?? "")) return node;
  return cloneElement(node, { children: Children.toArray(node.props.children).map(child => traverse(child, visit)) });
}

/** Split authored React prose, retaining inline links and original text in initial HTML. */
export function QuestionBody({ children }: { children: ReactNode }) {
  const path = useRouterState({ select: s => s.location.pathname });
  const enabled = path.startsWith("/industries/") || path.startsWith("/services/") || path.startsWith("/glossary/") || path.endsWith("-ppc-agency");
  if (!enabled) return <>{children}</>;
  let count = 0;
  Children.toArray(children).forEach(node => traverse(node, p => { count++; return p; }));
  if (count < 2) return <>{children}</>;
  let index = 0;
  let first = true;
  const row = (content: ReactNode) => <div className="question-row"><span className="question-number" aria-hidden="true">{String(++index).padStart(2, "0")}</span><div className="question-row-copy">{content}</div></div>;
  return <div className="question-body">{Children.toArray(children).map(node => traverse(node, p => {
    if (!first) return row(p);
    first = false;
    const value = text(p);
    // Sentence punctuation followed by whitespace, not decimal points or URL punctuation.
    const boundary = /[.!?](?:[”’"']?)(?=\s|$)/.exec(value);
    const end = boundary ? boundary.index + boundary[0].length : value.length;
    const lead = slice(p, 0, end);
    const remainder = slice(p, end, value.length);
    return <>{isValidElement<NodeProps>(lead) ? cloneElement(lead, { className: "question-answer" }) : lead}{value.slice(end).trim() ? row(remainder) : null}</>;
  }))}</div>;
}
